import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';

import { studentBillingApi } from '@/api';
import {
  Alert,
  Badge,
  Button,
  Card,
  CardHeader,
  ConfirmDialog,
  ErrorState,
  Icon,
  LoadingState,
  PageHeader,
  Pagination,
  useToast,
} from '@/components/ui';
import { InvoiceTable } from '@/features/billing/InvoiceTable';
import { formatMoney, formatPrice, statusLabel, statusTone } from '@/features/billing/money';
import { CHANGE, planButtonLabel, planChangeKind } from '@/features/billing/planChange';
import { InvoiceDownloadButton } from '@/features/studentBilling/InvoiceDownloadButton';
import { FREE_PLAN_CODE, yearlySaving } from '@/features/studentBilling/plans';
import { StudentPlanCard } from '@/features/studentBilling/StudentPlanCard';
import { StudentUpgradeDialog } from '@/features/studentBilling/StudentUpgradeDialog';
import { StudentUsageMeters } from '@/features/studentBilling/StudentUsageMeters';
import { PlanLockedAction } from '@/features/billing/PlanLockedAction';
import { errorMessage } from '@/hooks/useApiError';
import { formatDate } from '@/utils/format';

const PAGE_SIZE = 10;
const KEYS = ['student-billing', 'student-billing-plans', 'student-invoices'];

/**
 * The student's plan, this month's usage, and their payments.
 *
 * Buttons keep their plain wording ("Upgrade", "Switch to monthly/yearly",
 * "Switch to Free"); when a change takes effect -- now, or when the current
 * period ends -- is said in the confirm dialog. With
 * no payment gateway an upgrade is active at once and its invoice waits for a
 * bank transfer ("Active — invoice … awaiting payment"); with one, the plan
 * starts when the invoice is paid. A pending downgrade or cancellation can be
 * undone ("Keep my current plan"). The page repeats what the API answered and
 * every failure is a toast with the API's message. A coupon is entered in the
 * change dialog and comes off the invoice.
 */
export function StudentPlanPage() {
  const queryClient = useQueryClient();
  const toast = useToast();
  const [upgrading, setUpgrading] = useState(null);
  const [cancelling, setCancelling] = useState(false);
  const [page, setPage] = useState(1);

  const billing = useQuery({
    queryKey: ['student-billing'],
    queryFn: studentBillingApi.getBilling,
  });
  const plans = useQuery({
    queryKey: ['student-billing-plans'],
    queryFn: studentBillingApi.listPlans,
  });
  const invoices = useQuery({
    queryKey: ['student-invoices', page],
    queryFn: () => studentBillingApi.listInvoices({ page, page_size: PAGE_SIZE }),
    placeholderData: keepPreviousData,
  });

  function refresh() {
    KEYS.forEach((key) => queryClient.invalidateQueries({ queryKey: [key] }));
  }

  const subscribe = useMutation({
    mutationFn: ({ planCode, couponCode }) => studentBillingApi.changePlan(planCode, couponCode),
    onSuccess: (result) => {
      setUpgrading(null);
      toast.success(result.message);
      if (result.checkout_url) window.location.assign(result.checkout_url);
      refresh();
    },
    onError: (error) => {
      setUpgrading(null);
      toast.error(errorMessage(error));
    },
  });

  const cancel = useMutation({
    mutationFn: studentBillingApi.cancel,
    onSuccess: (result) => {
      setCancelling(false);
      toast.success(result.message);
      refresh();
    },
    onError: (error) => {
      setCancelling(false);
      toast.error(errorMessage(error));
    },
  });

  const resume = useMutation({
    mutationFn: studentBillingApi.resume,
    onSuccess: (result) => {
      toast.success(result.message);
      refresh();
    },
    onError: (error) => toast.error(errorMessage(error)),
  });

  if (billing.isPending || plans.isPending) {
    return <LoadingState label="Loading your plan…" />;
  }
  if (billing.isError || plans.isError) {
    const error = billing.error ?? plans.error;
    return (
      <ErrorState
        message={errorMessage(error)}
        onRetry={() => {
          void billing.refetch();
          void plans.refetch();
        }}
      />
    );
  }

  const current = billing.data;
  const pending = current.pending_invoice;
  const catalogue = plans.data ?? [];
  const saving = yearlySaving(catalogue);
  const onPaidPlan = current.is_paid;
  const periodEnd = current.current_period_end;
  const scheduled = current.scheduled_plan;
  // The plan was started before payment (no gateway): it is active and its
  // invoice is only waiting for the transfer.
  const awaitingPayment = Boolean(pending && onPaidPlan && !current.starts_on_payment);
  const paying = {
    code: current.plan.code,
    price_cents: current.plan.price_cents,
    currency: current.plan.currency,
    interval: current.plan.interval,
    running: onPaidPlan,
  };

  function planAction(plan) {
    const kind = planChangeKind(plan, paying);
    if (plan.is_current || kind === CHANGE.CURRENT) {
      return (
        <Button variant="secondary" className="w-full" disabled>
          Current plan
        </Button>
      );
    }
    if (scheduled?.code === plan.code) {
      return (
        <Button variant="secondary" className="w-full" disabled>
          Scheduled from {formatDate(periodEnd)}
        </Button>
      );
    }
    if (current.starts_on_payment && pending?.plan_code === plan.code) {
      return (
        <Button variant="secondary" className="w-full" disabled>
          Waiting for payment
        </Button>
      );
    }
    if (plan.locked_reason) {
      return <PlanLockedAction reason={plan.locked_reason} />;
    }
    if (plan.code === FREE_PLAN_CODE || plan.price_cents === 0) {
      // Only reachable from a paid plan: going back to Free is cancelling.
      if (!onPaidPlan) {
        return (
          <Button variant="secondary" className="w-full" disabled>
            Free
          </Button>
        );
      }
      return current.cancel_at_period_end ? (
        <Button variant="secondary" className="w-full" disabled>
          From {formatDate(periodEnd)}
        </Button>
      ) : (
        <Button variant="secondary" className="w-full" onClick={() => setCancelling(true)}>
          Switch to Free
        </Button>
      );
    }
    return (
      <Button className="w-full" onClick={() => setUpgrading({ plan, kind })}>
        <Icon name="sparkles" size={16} />
        {planButtonLabel(plan, { onPaidPlan })}
      </Button>
    );
  }

  const invoiceRows = invoices.data?.data ?? [];
  const meta = invoices.data?.meta;

  return (
    <div>
      <PageHeader
        title="Plan & billing"
        description="Your plan, what you have used this month, and your payments."
      />

      {pending && (
        <Alert
          tone={awaitingPayment ? 'info' : 'warning'}
          className="mb-6"
          title={
            awaitingPayment
              ? `Active — invoice ${pending.number} awaiting payment`
              : `Invoice ${pending.number} is waiting for payment`
          }
        >
          <div className="space-y-3">
            <p>
              {formatMoney(pending.amount_cents, pending.currency)} for {pending.description}
              {pending.discount_cents > 0 &&
                ` (${formatMoney(pending.discount_cents, pending.currency)} off with ${pending.coupon_code})`}
              . Due {formatDate(pending.due_at)}.{' '}
              {awaitingPayment
                ? 'Your plan is already active; we mark the invoice paid when the transfer arrives.'
                : 'Your plan starts as soon as it is paid.'}
            </p>
            {current.payment_instructions && (
              <p className="border-warning-200 rounded-input border bg-white/60 px-3 py-2">
                {current.payment_instructions}
              </p>
            )}
            <div className="flex flex-wrap gap-2">
              <Button
                variant="secondary"
                size="sm"
                onClick={() =>
                  void studentBillingApi
                    .downloadInvoicePdf(pending)
                    .catch((error) => toast.error(errorMessage(error)))
                }
              >
                <Icon name="download" size={16} />
                Download invoice
              </Button>
              {!onPaidPlan && (
                <Button variant="ghostDanger" size="sm" onClick={() => setCancelling(true)}>
                  Withdraw request
                </Button>
              )}
            </div>
          </div>
        </Alert>
      )}

      {onPaidPlan && (scheduled || current.cancel_at_period_end) ? (
        <Alert
          tone="info"
          className="mb-6"
          title={
            scheduled
              ? `Moving to ${scheduled.name} on ${formatDate(periodEnd)}`
              : `${current.plan.name} ends on ${formatDate(periodEnd)}`
          }
        >
          <div className="flex flex-wrap items-center gap-3">
            <span>
              {scheduled
                ? `You keep everything ${current.plan.name} gives you until then.${
                    current.scheduled_coupon_code
                      ? ` Code ${current.scheduled_coupon_code} will be applied to its first bill.`
                      : ''
                  }`
                : 'You keep everything it gives you until then, then move to Student Free.'}
            </span>
            <Button
              variant="secondary"
              size="sm"
              onClick={() => resume.mutate()}
              isLoading={resume.isPending}
            >
              Keep my current plan
            </Button>
          </div>
        </Alert>
      ) : (
        !pending &&
        current.notice && (
          <Alert tone="info" className="mb-6" title="About your plan">
            {current.notice}
          </Alert>
        )
      )}

      <div className="mb-6 grid items-start gap-6 lg:grid-cols-[1fr_340px]">
        <Card>
          <CardHeader
            title="This month"
            description="Allowances reset on the 1st of each month. Only new AI answers count — opening one again is free."
          />
          <StudentUsageMeters usage={current.usage} />
        </Card>

        <Card className="border-brand-200 bg-brand-50/40">
          <CardHeader
            title="Your plan"
            action={
              <Badge tone="brand" dot>
                Current plan
              </Badge>
            }
          />
          <div className="flex flex-wrap items-center gap-2">
            <p className="text-ink text-xl font-semibold">{current.plan.name}</p>
            {onPaidPlan && (
              <Badge dot tone={statusTone(current.status)}>
                {statusLabel(current.status)}
              </Badge>
            )}
          </div>
          {current.plan.price_cents > 0 && (
            <p className="text-ink mt-1 text-2xl font-semibold tracking-tight tabular-nums">
              {formatPrice(current.plan.price_cents, current.plan.currency, current.plan.interval)}
            </p>
          )}
          <dl className="border-brand-100 mt-4 space-y-2 border-t pt-4 text-sm">
            {current.current_period_end && (
              <div className="flex justify-between gap-3">
                <dt className="text-ink-muted">
                  {current.cancel_at_period_end
                    ? 'Ends'
                    : awaitingPayment
                      ? 'Current period ends'
                      : 'Paid until'}
                </dt>
                <dd className="text-ink">{formatDate(current.current_period_end)}</dd>
              </div>
            )}
            <div className="flex justify-between gap-3">
              <dt className="text-ink-muted">Premium badge</dt>
              <dd className="text-ink">
                {current.application_boost ? 'On your applications' : 'Not included'}
              </dd>
            </div>
          </dl>
          {onPaidPlan && !current.cancel_at_period_end && !scheduled && (
            <Button variant="ghost" size="sm" className="mt-4" onClick={() => setCancelling(true)}>
              Cancel at period end
            </Button>
          )}
        </Card>
      </div>

      <h2 className="text-ink mb-4 text-base font-semibold tracking-tight">Plans</h2>
      <div className="grid gap-4 md:grid-cols-3">
        {catalogue.map((plan) => (
          <StudentPlanCard
            key={plan.code}
            plan={plan}
            saving={saving?.code === plan.code ? saving : null}
            action={planAction(plan)}
          />
        ))}
      </div>

      <Card className="mt-6">
        <CardHeader title="Payment history" />
        {invoices.isPending ? (
          <LoadingState label="Loading invoices…" />
        ) : invoices.isError ? (
          <ErrorState
            message={errorMessage(invoices.error)}
            onRetry={() => void invoices.refetch()}
          />
        ) : (
          <>
            <InvoiceTable
              invoices={invoiceRows}
              emptyTitle="No payments yet"
              emptyDescription="Student Free costs nothing, so there is nothing here until you upgrade."
              action={(invoice) => (
                <InvoiceDownloadButton
                  invoice={invoice}
                  download={studentBillingApi.downloadInvoicePdf}
                />
              )}
            />
            {meta && meta.total_pages > 1 && (
              <Pagination
                className="mt-4"
                page={meta.page}
                totalPages={meta.total_pages}
                totalItems={meta.total_items}
                pageSize={PAGE_SIZE}
                onPageChange={setPage}
              />
            )}
          </>
        )}
      </Card>

      {upgrading && (
        <StudentUpgradeDialog
          key={upgrading.plan.code}
          plan={upgrading.plan}
          kind={upgrading.kind}
          currentName={current.plan.name}
          periodEnd={periodEnd}
          isLoading={subscribe.isPending}
          onCancel={() => setUpgrading(null)}
          onConfirm={(planCode, couponCode) => subscribe.mutate({ planCode, couponCode })}
        />
      )}

      <ConfirmDialog
        open={cancelling}
        title={onPaidPlan ? 'Switch to Student Free?' : 'Withdraw your upgrade request?'}
        message={
          onPaidPlan
            ? `Starts on ${formatDate(current.current_period_end)}, when your current period ends. You keep ${current.plan.name} until then.`
            : 'The unpaid invoice will be cancelled. Do not pay it after withdrawing.'
        }
        confirmLabel={onPaidPlan ? 'Confirm switch' : 'Withdraw'}
        isLoading={cancel.isPending}
        onCancel={() => setCancelling(false)}
        onConfirm={() => cancel.mutate()}
      />
    </div>
  );
}
